import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/hooks/use-language';
import { usePageHero } from '@/hooks/usePageHero';
import { Send, CheckCircle2, ChevronDown, Check, ArrowRight, ArrowLeft, ShieldCheck, Zap, BarChart3, Rocket, Search, Star, Award, TrendingUp, Users, Globe, Package, Heart, Handshake, Building2, Lightbulb, Target, Layers, Repeat } from 'lucide-react';
import { cn } from '@/lib/utils';
import { dealerLeadsDb, becomeDealerContentDb } from '@/lib/adminDb';
import { SEO } from '@/components/ui/SEO';
import { buildBreadcrumbs } from '@/lib/schema';
import { getPagePath, type Lang } from '@/lib/localizedRoutes';
import { trackFormSubmit, trackClick } from '@/lib/analytics';

// ─── CMS Cache (memory + sessionStorage) ────────────────────────────────────
// Двухуровневый кэш: memory для текущей сессии, sessionStorage для refresh.
// При смене языка показываем старые данные пока грузятся новые (no flicker).
interface CmsCache {
  intro: CmsIntro | null;
  left: CmsLeftBlock | null;
  checklist: CmsChecklist | null;
  cards: CmsCards | null;
}

const EMPTY_CACHE: CmsCache = { intro: null, left: null, checklist: null, cards: null };
const _memCache: Record<string, CmsCache> = {};
const _promises: Record<string, Promise<CmsCache>> = {};

function ssKey(lang: string) { return `sargo_bd_cms_${lang}`; }

function readSS(lang: string): CmsCache | null {
  try {
    const raw = sessionStorage.getItem(ssKey(lang));
    if (!raw) return null;
    return JSON.parse(raw) as CmsCache;
  } catch { return null; }
}

function writeSS(lang: string, data: CmsCache) {
  try { sessionStorage.setItem(ssKey(lang), JSON.stringify(data)); } catch { /* quota */ }
}

async function fetchCmsForLang(lang: string): Promise<CmsCache> {
  try {
    const res = await becomeDealerContentDb.listByLang(lang);
    if (!res.ok || !Array.isArray(res.data)) return EMPTY_CACHE;
    const map: Record<string, any> = {};
    res.data.forEach((block: any) => {
      try { map[block.blockKey] = JSON.parse(block.contentJson); } catch { /* skip */ }
    });
    return {
      intro:     map['intro']      ?? null,
      left:      map['left_block'] ?? null,
      checklist: map['checklist']  ?? null,
      cards:     map['cards']      ?? null,
    };
  } catch {
    return EMPTY_CACHE;
  }
}

function primeCache(lang: string): Promise<CmsCache> {
  // 1. memory hit
  if (_memCache[lang]) return Promise.resolve(_memCache[lang]);
  // 2. already fetching
  if (_promises[lang]) return _promises[lang];
  // 3. fetch and store both levels
  _promises[lang] = fetchCmsForLang(lang).then(data => {
    _memCache[lang] = data;
    writeSS(lang, data);
    return data;
  });
  return _promises[lang];
}

// Стартуем prefetch для всех трёх языков сразу при импорте модуля.
// К моменту первого рендера ru обычно уже готов.
primeCache('ru');
primeCache('uz');
primeCache('en');

// ─── Icon resolver (mirrors BecomeDealerAdmin) ──────────────────────────────
const ICON_MAP: Record<string, React.ElementType> = {
  shield: ShieldCheck, zap: Zap, chart: BarChart3, rocket: Rocket,
  star: Star, award: Award, trending: TrendingUp, users: Users,
  globe: Globe, package: Package, check: CheckCircle2, heart: Heart,
  handshake: Handshake, building: Building2, lightbulb: Lightbulb,
  target: Target, layers: Layers, repeat: Repeat,
};
function resolveIcon(key: string): React.ElementType {
  return ICON_MAP[key] ?? ShieldCheck;
}

// ─── CMS types ───────────────────────────────────────────────────────────────
interface CmsIntro { eyebrow: string; title: string; subtitle: string; }
interface CmsLeftBlock { title: string; description: string; }
interface CmsChecklist { items: string[]; }
interface CmsCardItem { id: string; icon: string; title: string; description: string; }
interface CmsCards { items: CmsCardItem[]; }

// ─── Hook: мгновенный рендер из кэша ─────────────────────────────────────────
function useCmsContent(lang: string) {
  // Инициализируем state синхронно: memory → sessionStorage → empty
  function getInitial(l: string): CmsCache {
    return _memCache[l] ?? readSS(l) ?? EMPTY_CACHE;
  }

  const [data, setData] = useState<CmsCache>(() => getInitial(lang));

  // CRITICAL: synchronously swap state to new-language cache during render
  // so the first render after a language change never shows previous-language
  // content. React 18 supports the "derive state from props during render"
  // pattern when guarded by a ref-based change detector.
  const lastLangRef = useRef(lang);
  if (lastLangRef.current !== lang) {
    lastLangRef.current = lang;
    setData(getInitial(lang));
  }

  useEffect(() => {
    // memory hit — уже готово, обновляем на случай если state не актуален
    if (_memCache[lang]) {
      setData(_memCache[lang]);
      return;
    }
    // sessionStorage hit — рендерим сразу, фоново обновляем из сети
    const ss = readSS(lang);
    if (ss) setData(ss);

    // Подключаемся к промису (он уже запущен при импорте) и игнорируем
    // устаревший ответ, если язык успел переключиться к моменту resolve.
    let cancelled = false;
    primeCache(lang).then(d => {
      if (!cancelled) setData(d);
    });
    return () => { cancelled = true; };
  }, [lang]);

  // ready=true когда есть хоть что-то для отображения
  const ready = !!(data.intro || data.left || data.checklist || data.cards);
  return { data, ready };
}

// COMPLETE REFACTOR: Static country data with maxDigits for phone validation
interface Country {
  iso: string;
  name: {
    ru: string;
    en: string;
    uz: string;
  };
  dial: string;
  maxDigits: number;
}

const COUNTRIES: Country[] = [
  // CIS Countries
  { iso: 'UZ', name: { ru: 'Узбекистан', en: 'Uzbekistan', uz: "O'zbekiston" }, dial: '+998', maxDigits: 9 },
  { iso: 'RU', name: { ru: 'Россия', en: 'Russia', uz: 'Rossiya' }, dial: '+7', maxDigits: 10 },
  { iso: 'KZ', name: { ru: 'Казахстан', en: 'Kazakhstan', uz: "Qozog'iston" }, dial: '+7', maxDigits: 10 },
  { iso: 'KG', name: { ru: 'Кыргызстан', en: 'Kyrgyzstan', uz: "Qirg'iziston" }, dial: '+996', maxDigits: 9 },
  { iso: 'TJ', name: { ru: 'Таджикистан', en: 'Tajikistan', uz: 'Tojikiston' }, dial: '+992', maxDigits: 9 },
  { iso: 'TM', name: { ru: 'Туркменистан', en: 'Turkmenistan', uz: 'Turkmaniston' }, dial: '+993', maxDigits: 8 },
  { iso: 'AM', name: { ru: 'Армения', en: 'Armenia', uz: 'Armaniston' }, dial: '+374', maxDigits: 8 },
  { iso: 'AZ', name: { ru: 'Азербайджан', en: 'Azerbaijan', uz: 'Ozarbayjon' }, dial: '+994', maxDigits: 9 },
  { iso: 'BY', name: { ru: 'Беларусь', en: 'Belarus', uz: 'Belarus' }, dial: '+375', maxDigits: 9 },
  { iso: 'GE', name: { ru: 'Грузия', en: 'Georgia', uz: 'Gruziya' }, dial: '+995', maxDigits: 9 },
  { iso: 'UA', name: { ru: 'Украина', en: 'Ukraine', uz: 'Ukraina' }, dial: '+380', maxDigits: 9 },
  { iso: 'MD', name: { ru: 'Молдова', en: 'Moldova', uz: 'Moldova' }, dial: '+373', maxDigits: 8 },
  // Major Countries
  { iso: 'US', name: { ru: 'США', en: 'United States', uz: 'AQSH' }, dial: '+1', maxDigits: 10 },
  { iso: 'GB', name: { ru: 'Великобритания', en: 'United Kingdom', uz: 'Buyuk Britaniya' }, dial: '+44', maxDigits: 10 },
  { iso: 'TR', name: { ru: 'Турция', en: 'Turkey', uz: 'Turkiya' }, dial: '+90', maxDigits: 10 },
  { iso: 'AE', name: { ru: 'ОАЭ', en: 'United Arab Emirates', uz: 'BAA' }, dial: '+971', maxDigits: 9 },
  { iso: 'DE', name: { ru: 'Германия', en: 'Germany', uz: 'Germaniya' }, dial: '+49', maxDigits: 11 },
  { iso: 'FR', name: { ru: 'Франция', en: 'France', uz: 'Fransiya' }, dial: '+33', maxDigits: 9 },
];

export const BecomeDealer: React.FC<{ seoEnabled?: boolean }> = ({ seoEnabled = true }) => {
  const { t, language } = useLanguage();
  const dealerHero = usePageHero({
    pageKey: 'becomedealer',
    lang: language,
    fallbacks: {
      eyebrow: t('page.becomeDealer.network'),
      title: t('page.becomeDealer.title'),
    },
  });

  // ─── CMS Content (eager cache, no skeleton) ───────────────────────────────
  // Перед рендером данные уже загружены (primeCache при импорте).
  // Если язык сменился — подключаемся к уже идущему промису.
  const { data: cms, ready: cmsReady } = useCmsContent(language);
  const cmsIntro     = cms.intro;
  const cmsLeft      = cms.left;
  const cmsChecklist = cms.checklist;
  const cmsCards     = cms.cards;
  // cmsReady=true означает что данные уже в памяти — skeleton не нужен.
  // При первом посещении (язык ru) данные готовы сразу.
  // При смене языка — быстрая догрузка без показа skeleton.

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedJourneyStep, setSelectedJourneyStep] = useState<number | null>(null);
  
  // NEW: Proper country selection state
  const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0]);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    city: '',
    phone: '', // Stores only digits (no country code)
    email: '',
    message: ''
  });

  // NEW: Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        console.log('Country dropdown closed by click outside');
        setIsCountryDropdownOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isCountryDropdownOpen) {
        console.log('Country dropdown closed by ESC key');
        setIsCountryDropdownOpen(false);
      }
    };

    if (isCountryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      
      // Handle position flip
      const updatePosition = () => {
        if (dropdownRef.current) {
          const rect = dropdownRef.current.getBoundingClientRect();
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;
          
          if (spaceBelow < 350 && spaceAbove > spaceBelow) {
            setDropdownPosition('top');
          } else {
            setDropdownPosition('bottom');
          }
        }
      };
      
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);

      // Focus search input and reset scroll
      setTimeout(() => {
        searchInputRef.current?.focus();
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      }, 50);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isCountryDropdownOpen]);

  // Filter countries based on search query
  const filteredCountries = COUNTRIES.filter(country => {
    const search = searchQuery.toLowerCase();
    const name = (language === 'ru' ? country.name.ru : 
                 language === 'uz' ? country.name.uz : 
                 country.name.en).toLowerCase();
    return name.includes(search) || 
           country.dial.includes(search) || 
           country.iso.toLowerCase().includes(search);
  });

  // No animations for sections



  const validateStep = (step: number) => {
    const newErrors: { [key: string]: string } = {};

    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = t('dealer.form.error.required');
    } else if (step === 2) {
      if (!formData.company.trim()) newErrors.company = t('dealer.form.error.required');
      if (!formData.city.trim()) newErrors.city = t('dealer.form.error.required');
      if (formData.phone.length < 7) newErrors.phone = t('dealer.form.error.phone');
    } else if (step === 3) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData.email || !emailRegex.test(formData.email)) {
        newErrors.email = t('dealer.form.error.email');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep(3)) return;

    setIsSubmitting(true);
    try {
      const fullPhone = `${selectedCountry.dial}${formData.phone}`;
      const id = `lead_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

      const result = await dealerLeadsDb.upsert({
        id,
        name: formData.name.trim(),
        company: formData.company.trim(),
        city: formData.city.trim(),
        phone: fullPhone,
        email: formData.email.trim(),
        message: formData.message.trim(),
        status: 'new',
        notes: '',
        read: 0,
        source: 'become-dealer',
        ip: '',
        createdAt: new Date().toISOString(),
      });

      if (!result.ok) {
        throw new Error(result.error || 'Ошибка сохранения заявки');
      }

      trackFormSubmit('dealer_application', '/become-dealer');

      setIsSubmitted(true);
    } catch (err: any) {
      console.error('[BecomeDealer] submit error:', err);
      alert(err?.message || 'Произошла ошибка. Попробуйте ещё раз.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // NEW: Handle input changes with phone digit restrictions
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Only allow digits
      const digitsOnly = value.replace(/\D/g, '');
      // Restrict to maxDigits for selected country
      const restricted = digitsOnly.slice(0, selectedCountry.maxDigits);
      console.log(`Phone input: "${digitsOnly}" -> restricted to ${restricted.length}/${selectedCountry.maxDigits} digits for ${selectedCountry.iso}`);
      setFormData({ ...formData, [name]: restricted });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  // NEW: Handle country selection
  const handleCountrySelect = (country: Country) => {
    console.log(`Country selected: ${country.iso} ${country.dial} (max ${country.maxDigits} digits)`);
    setSelectedCountry(country);
    setIsCountryDropdownOpen(false);
    
    // If phone number exceeds new country's maxDigits, truncate it
    if (formData.phone.length > country.maxDigits) {
      const truncated = formData.phone.slice(0, country.maxDigits);
      setFormData({ ...formData, phone: truncated });
      console.log(`Phone truncated from ${formData.phone.length} to ${truncated.length} digits`);
    }
  };

  // NEW: Toggle dropdown with logging
  const toggleCountryDropdown = () => {
    const newState = !isCountryDropdownOpen;
    console.log('Country dropdown toggle', newState);
    if (newState) setSearchQuery('');
    setIsCountryDropdownOpen(newState);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      company: '',
      city: '',
      phone: '',
      email: '',
      message: ''
    });
    setIsSubmitted(false);
    setCurrentStep(1);
    setErrors({});
  };

  const isCurrentStepValid = () => {
    if (currentStep === 1) {
      return formData.name.trim().length > 0;
    }
    if (currentStep === 2) {
      return formData.company.trim().length > 0 && 
             formData.city.trim().length > 0 && 
             formData.phone.length >= 7;
    }
    if (currentStep === 3) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(formData.email);
    }
    return false;
  };

  return (
    <div className="pb-16 lg:pb-24">
      <SEO
        enabled={seoEnabled}
        title={language === 'uz' ? "SARGO dileri bo'ling | Sheriklik dasturi" : language === 'en' ? 'Become a SARGO Dealer | Partnership Programme' : 'Стать дилером SARGO | Партнерская программа'}
        description={language === 'uz'
          ? "SARGO dileri bo'ling: sheriklik dasturi, eksklyuziv shartlar, marketing qo'llab-quvvatlash va savdoni rivojlantirish imkoniyatlari."
          : language === 'en'
            ? 'Become a SARGO dealer: partnership programme, exclusive terms, marketing support and sales development opportunities.'
            : 'Станьте дилером SARGO: партнёрская программа, эксклюзивные условия, маркетинговая поддержка и возможности развития продаж.'}
        canonical={getPagePath('become-dealer', language as Lang)}
        schema={buildBreadcrumbs(window.location.origin, [
          { name: language === 'uz' ? 'Bosh sahifa' : language === 'en' ? 'Home' : 'Главная', url: `/${language}` },
          { name: language === 'uz' ? 'Diler bo\'ling' : language === 'en' ? 'Become a Dealer' : 'Стать дилером', url: getPagePath('become-dealer', language as Lang) },
        ])}
      />
      {/* Header */}
      <section className="pt-24 md:pt-32 pb-8 lg:pb-12 relative">
        <div className="grid-cells opacity-50" />
        <div className="container-custom relative z-10">
          <div className="space-y-4 lg:space-y-6">
            {dealerHero.eyebrow?.trim() && (
              <div className="flex items-center gap-2 lg:gap-3">
                <div className="w-1 h-1 lg:w-1.5 lg:h-1.5 bg-primary shadow-[0_0_10px_rgba(176,138,90,1)]"></div>
                <p className="text-primary font-black uppercase tracking-[0.4em] text-[10px]">{dealerHero.eyebrow}</p>
              </div>
            )}
            {dealerHero.title?.trim() && (
              <div>
                <h1 className="font-black uppercase tracking-tighter leading-[0.95] lg:leading-[0.8]">
                  {dealerHero.title}<span className="text-primary">.</span>
                </h1>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Intro block — under hero, CMS-driven */}
      {cmsIntro && (cmsIntro.eyebrow || cmsIntro.title || cmsIntro.subtitle) && (
        <section className="pb-10 lg:pb-14 relative">
          <div className="container-custom relative z-10">
            <div className="space-y-3 lg:space-y-5 max-w-3xl">
              {cmsIntro.eyebrow?.trim() && (
                <div className="flex items-center gap-2 lg:gap-3">
                  <div className="w-1 h-1 lg:w-1.5 lg:h-1.5 bg-primary shadow-[0_0_10px_rgba(176,138,90,1)]" />
                  <p className="text-primary font-black uppercase tracking-[0.4em] text-[10px]">{cmsIntro.eyebrow}</p>
                </div>
              )}
              {cmsIntro.title?.trim() && (
                <h2 className="font-black uppercase tracking-tighter leading-[0.95] text-2xl lg:text-4xl">
                  {cmsIntro.title}
                </h2>
              )}
              {cmsIntro.subtitle?.trim() && (
                <p className="text-white/50 text-base lg:text-lg leading-relaxed">{cmsIntro.subtitle}</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="pb-16 lg:pb-24 relative">
        <div className="grid-cells-accent opacity-5" />
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            
            {/* Left Column: Dealer Journey */}
            <div className="space-y-12 lg:space-y-16">
              <div className="space-y-6">
                {cmsReady && cmsLeft?.title && (
                  <h2 className="text-4xl lg:text-5xl font-extrabold uppercase tracking-tighter">
                    {cmsLeft.title}
                  </h2>
                )}
                {cmsReady && cmsLeft?.description && (
                  <p className="text-white/50 text-lg leading-relaxed max-w-xl">
                    {cmsLeft.description}
                  </p>
                )}

                {/* Checklist — CMS-driven only, no skeleton */}
                {cmsReady && cmsChecklist?.items && cmsChecklist.items.length > 0 && (
                  <div className="flex flex-col gap-3 pt-4">
                    {cmsChecklist.items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center border border-primary/30">
                          <Check size={12} className="text-primary" />
                        </div>
                        <span className="text-sm font-medium text-white/70 uppercase tracking-wider">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Journey Cards Grid 2x2 — CMS-driven only, no skeleton */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
                {cmsReady && cmsCards?.items?.map((card, idx) => {
                      const IconComp = resolveIcon(card.icon);
                      const isSelected = selectedJourneyStep === idx;
                      return (
                        <div
                          key={card.id}
                          onClick={() => setSelectedJourneyStep(isSelected ? null : idx)}
                          className={cn(
                            "noise-bg p-6 lg:p-8 space-y-3 lg:space-y-4 group cursor-pointer h-full relative bg-[#13100B]",
                            isSelected
                              ? "border-primary shadow-[0_0_20px_rgba(176,138,90,0.2)]"
                              : "border-white/10 hover:border-white/20",
                            "border rounded-none md:max-lg:p-5 sm:max-md:p-4"
                          )}
                        >
                          <div className="relative z-20 space-y-3 lg:space-y-4">
                            <div className={cn(
                              "group-hover:-translate-y-1.5",
                              isSelected ? "text-primary brightness-125" : "text-primary/60 group-hover:text-primary group-hover:brightness-125"
                            )}>
                              <IconComp className="w-6 h-6 lg:w-8 lg:h-8" strokeWidth={1.5} />
                            </div>
                            <div className="space-y-1.5 lg:space-y-2">
                              <h3 className={cn(
                                "text-sm lg:text-base font-bold uppercase tracking-widest",
                                isSelected ? "text-primary" : "text-primary/70 group-hover:text-primary"
                              )}>
                                {card.title}
                              </h3>
                              <p className="text-white/40 text-[10px] lg:text-xs leading-relaxed">{card.description}</p>
                            </div>
                          </div>
                          <div className={cn("mirror-reflection opacity-0 lg:group-hover:opacity-10 hidden lg:block", isSelected && "lg:opacity-5")} />
                          <div className={cn("shine-overlay opacity-0 lg:group-hover:opacity-10 hidden lg:block", isSelected && "lg:opacity-5")} />
                        </div>
                      );
                    })
                }
              </div>
            </div>

            {/* Right Column: Wizard Form */}
            <div className="relative lg:sticky lg:top-32">
              <div
              className={cn(
                "noise-bg p-5 md:p-8 lg:p-12 space-y-4 md:space-y-6 lg:space-y-8 !overflow-visible bg-[#13100B]",
                "border shadow-[0_0_50px_-12px_rgba(176,138,90,0.05)]",
                // Desktop: original fixed border color
                "lg:border-[#B08A5A]/10",
                // Mobile/Tablet: dynamic border color
                currentStep <= 3 && (isCurrentStepValid() || isSubmitting)
                  ? "max-lg:border-[#B08A5A] max-lg:shadow-[0_0_20px_rgba(176,138,90,0.2)]"
                  : "max-lg:border-white/10"
              )}
              >
                {!isSubmitted ? (
                  <div className="space-y-6 md:space-y-7 lg:space-y-8">
                    {/* Progress Bar */}
                    <div className="space-y-3 lg:space-y-4">
                      <div className="flex justify-between items-end">
                        <span className="text-[9px] lg:text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
                          {t('dealer.form.wizard.step').replace('{x}', currentStep.toString())}
                        </span>
                        <span className="text-[9px] lg:text-[10px] font-bold uppercase tracking-[0.3em] text-white/20">
                          {Math.round((currentStep / 3) * 100)}%
                        </span>
                      </div>
                      <div className="h-[2px] w-full bg-white/5 relative">
                        <div 
                          className="absolute inset-y-0 left-0 bg-primary shadow-[0_0_10px_rgba(176,138,90,0.5)]"
                          style={{ width: `${(currentStep / 3) * 100}%` }}
                        />
                      </div>
                    </div>

                    <form onSubmit={handleSubmit} className="relative min-h-0 lg:min-h-[320px] flex flex-col">
                        {currentStep === 1 && (
                          <div
                            key="step1"
                            className="space-y-4 lg:space-y-6"
                          >
                            <div className="space-y-1.5 lg:space-y-2">
                              <label className="text-[9px] lg:text-[10px] font-bold uppercase tracking-widest text-white/30 ml-2">
                                {t('dealer.form.name')} *
                              </label>
                              <input 
                                required
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                type="text" 
                                placeholder={t('dealer.form.name.placeholder')}
                                className={cn(
                                  "w-full bg-[#13100B]/50 lg:bg-background/50 border py-2 md:py-2.5 lg:py-4 px-4 md:px-5 lg:px-6 text-[16px] lg:text-[10px] font-bold uppercase tracking-widest text-white focus:outline-none focus:border-primary placeholder:text-[10px] md:placeholder:text-[11px] lg:placeholder:text-[10px]",
                                  errors.name ? "border-primary/50" : "border-white/10 md:border-white/20 lg:border-white/10"
                                )}
                              />
                              {errors.name && <p className="text-[8px] lg:text-[9px] text-primary uppercase font-bold ml-2 mt-1">{errors.name}</p>}
                            </div>
                            <div className="pt-2 md:pt-4">
                              <button 
                                type="button"
                                onClick={nextStep}
                                className="mirror-button w-full clip-path-none group !px-4 !py-2.5 md:!px-6 md:!py-3 lg:!px-8 lg:!py-4"
                              >
                                <span className="flex items-center gap-3 relative z-10 text-[11px] md:text-sm lg:text-[10px]">
                                  {t('dealer.form.wizard.next')} <ArrowRight size={14} className="group-hover:translate-x-1" />
                                </span>
                              </button>
                            </div>
                          </div>
                        )}

                        {currentStep === 2 && (
                          <div
                            key="step2"
                            className="space-y-3 lg:space-y-6"
                          >
                          <div className="space-y-1 lg:space-y-2">
                            <label className="text-[9px] lg:text-[10px] font-bold uppercase tracking-widest text-white/30 ml-2">
                              {t('dealer.form.company')} *
                            </label>
                              <input 
                                required
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                type="text" 
                                placeholder={t('dealer.form.company.placeholder')}
                                className={cn(
                                  "w-full bg-[#13100B]/50 lg:bg-background/50 border py-2 md:py-2.5 lg:py-4 px-4 md:px-5 lg:px-6 text-[16px] lg:text-[10px] font-bold uppercase tracking-widest text-white focus:outline-none focus:border-primary placeholder:text-[10px] md:placeholder:text-[11px] lg:placeholder:text-[10px]",
                                  errors.company ? "border-primary/50" : "border-white/10 md:border-white/20 lg:border-white/10"
                                )}
                              />
                            {errors.company && <p className="text-[8px] lg:text-[9px] text-primary uppercase font-bold ml-2 mt-1">{errors.company}</p>}
                          </div>
                          
                          <div className="space-y-1 lg:space-y-2">
                            <label className="text-[9px] lg:text-[10px] font-bold uppercase tracking-widest text-white/30 ml-2">
                              {t('dealer.form.city')} *
                            </label>
                              <input 
                                required
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                type="text" 
                                placeholder={t('dealer.form.city.placeholder')}
                                className={cn(
                                  "w-full bg-[#13100B]/50 lg:bg-background/50 border py-2 md:py-2.5 lg:py-4 px-4 md:px-5 lg:px-6 text-[16px] lg:text-[10px] font-bold uppercase tracking-widest text-white focus:outline-none focus:border-primary placeholder:text-[10px] md:placeholder:text-[11px] lg:placeholder:text-[10px]",
                                  errors.city ? "border-primary/50" : "border-white/10 md:border-white/20 lg:border-white/10"
                                )}
                              />
                            {errors.city && <p className="text-[8px] lg:text-[9px] text-primary uppercase font-bold ml-2 mt-1">{errors.city}</p>}
                          </div>
                          
                          {/* NEW: Completely rebuilt phone field with working dropdown */}
                          <div className="space-y-1 lg:space-y-2">
                            <label className="text-[9px] lg:text-[10px] font-bold uppercase tracking-widest text-white/30 ml-2">
                              {t('dealer.form.phone')} *
                            </label>
                            <div className="flex gap-2">
                              {/* Country Code Selector */}
                              <div className="relative" ref={dropdownRef}>
                                <button
                                  type="button"
                                  onClick={toggleCountryDropdown}
                                  className="flex items-center gap-2 bg-[#13100B]/50 lg:bg-background/50 border border-white/10 md:border-white/20 lg:border-white/10 h-full px-3 md:px-4 py-2 md:py-2.5 lg:py-4 text-[10px] md:text-[11px] font-bold text-white hover:border-primary/50 whitespace-nowrap"
                                  >
                                  <span className="text-[13px] md:text-base lg:text-sm">{selectedCountry.iso}</span>
                                  <span className="text-white/50 text-[9px] md:text-[10px] lg:text-xs">{selectedCountry.dial}</span>
                                  <ChevronDown 
                                    size={12} 
                                    className={cn(
                                      "text-white/30",
                                      isCountryDropdownOpen && "rotate-180"
                                    )} 
                                  />
                                </button>
                                
                                {/* Dropdown - with flip logic and search */}
                                  {isCountryDropdownOpen && (
                                    <div
                                      className={cn(
                                        "absolute left-0 w-64 md:w-72 bg-[#13100B] border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex flex-col z-[9999]",
                                        dropdownPosition === 'bottom' ? "top-full mt-2" : "bottom-full mb-2"
                                      )}
                                    >
                                      {/* Sticky Search */}
                                      <div className="sticky top-0 z-20 bg-[#13100B] p-2 border-b border-white/10">
                                        <div className="relative">
                                          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                                          <input
                                            ref={searchInputRef}
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder={t('dealer.form.phone.search') || "Search..."}
                                            className="w-full bg-white/5 border border-white/10 py-2 md:py-2.5 pl-8 pr-4 text-[16px] lg:text-[10px] font-bold uppercase tracking-widest text-white focus:outline-none focus:border-primary/50 placeholder:text-[10px] md:placeholder:text-[11px] lg:placeholder:text-[10px]"
                                          />
                                        </div>
                                      </div>
                          
                                      {/* Country List Container */}
                                      <div 
                                        ref={scrollContainerRef}
                                        className="country-dropdown__list overflow-y-auto max-h-[240px] md:max-h-[280px] custom-scrollbar"
                                      >
                                        {filteredCountries.length > 0 ? (
                                          <div className="py-1">
                                            {filteredCountries.map((country) => {
                                              const countryName = language === 'ru' ? country.name.ru : 
                                                                language === 'uz' ? country.name.uz : 
                                                                country.name.en;
                                              
                                              return (
                                                <button
                                                  key={country.iso}
                                                  type="button"
                                                  onClick={() => handleCountrySelect(country)}
                                                  className={cn(
                                                    "w-full flex items-center justify-between px-3 md:px-4 py-2 md:py-3 lg:py-4 hover:bg-white/5 text-left",
                                                    selectedCountry.iso === country.iso && "bg-white/5"
                                                  )}
                                                  >
                                                  <div className="flex items-center gap-2 md:gap-3">
                                                    <span className="text-[12px] md:text-sm lg:text-[10px] font-bold uppercase tracking-widest text-white">
                                                      {language === 'ru' ? country.name.ru : language === 'uz' ? country.name.uz : language === 'en' ? country.name.en : country.name.en}
                                                    </span>
                                                  </div>
                                                  <span className="text-[11px] md:text-sm lg:text-[10px] font-bold text-primary">{country.dial}</span>
                                                  </button>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <div className="p-6 md:p-8 text-center">
                                            <p className="text-[9px] md:text-[10px] font-bold uppercase tracking-widest text-white/20">
                                              No results
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </div>
                          
                              {/* Phone Number Input */}
                              <input 
                                required
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                type="tel" 
                                placeholder={`${'0'.repeat(selectedCountry.maxDigits)}`}
                                maxLength={selectedCountry.maxDigits}
                                className={cn(
                                  "flex-1 bg-[#13100B]/50 lg:bg-background/50 border py-2 md:py-2.5 lg:py-4 px-4 md:px-5 lg:px-6 text-[16px] lg:text-[10px] font-bold uppercase tracking-widest text-white focus:outline-none focus:border-primary placeholder:text-[10px] md:placeholder:text-[11px] lg:placeholder:text-[10px]",
                                  errors.phone ? "border-primary/50" : "border-white/10 md:border-white/20 lg:border-white/10"
                                )}
                              />
                            </div>
                            {errors.phone && <p className="text-[8px] lg:text-[9px] text-primary uppercase font-bold ml-2 mt-1">{errors.phone}</p>}
                          </div>

                            <div className="pt-2 md:pt-4 flex gap-3 md:gap-4">
                              <button 
                                type="button"
                                onClick={prevStep}
                                className="mirror-button-outline flex-1 clip-path-none group border-white/10 text-white/50 hover:text-white hover:border-white/20 !px-3 !py-2 md:!px-4 md:!py-2.5 lg:!px-8 lg:!py-4"
                              >
                                <span className="flex items-center gap-2 md:gap-3 relative z-10 text-[11px] md:text-sm lg:text-[10px] whitespace-nowrap">
                                  <ArrowLeft size={14} className="group-hover:-translate-x-1" /> {t('dealer.form.wizard.back')}
                                </span>
                              </button>
                              <button 
                                type="button"
                                onClick={nextStep}
                                className="mirror-button flex-[2] clip-path-none group !px-3 !py-2 md:!px-4 md:!py-2.5 lg:!px-8 lg:!py-4"
                              >
                                <span className="flex items-center gap-2 md:gap-3 relative z-10 text-[11px] md:text-sm lg:text-[10px] whitespace-nowrap">
                                  {t('dealer.form.wizard.next_step')} <ArrowRight size={14} className="group-hover:translate-x-1" />
                                </span>
                              </button>
                            </div>
                          </div>
                        )}

                        {currentStep === 3 && (
                          <div
                            key="step3"
                            className="space-y-4 lg:space-y-6"
                          >
                            <div className="space-y-1 lg:space-y-2">
                              <label className="text-[9px] lg:text-[10px] font-bold uppercase tracking-widest text-white/30 ml-2">
                                {t('dealer.form.email')} *
                              </label>
                              <input 
                                required
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                type="email" 
                                placeholder="name@company.com"
                                className={cn(
                                  "w-full bg-[#13100B]/50 lg:bg-background/50 border py-2 md:py-2.5 lg:py-4 px-4 md:px-5 lg:px-6 text-[16px] lg:text-[10px] font-bold uppercase tracking-widest text-white focus:outline-none focus:border-primary placeholder:text-[10px] md:placeholder:text-[11px] lg:placeholder:text-[10px]",
                                  errors.email ? "border-primary/50" : "border-white/10 md:border-white/20 lg:border-white/10"
                                )}
                              />
                              {errors.email && <p className="text-[8px] lg:text-[9px] text-primary uppercase font-bold ml-2 mt-1">{errors.email}</p>}
                            </div>

                            <div className="space-y-1 lg:space-y-2">
                              <label className="text-[9px] lg:text-[10px] font-bold uppercase tracking-widest text-white/30 ml-2">
                                {t('dealer.form.message')}
                              </label>
                              <textarea 
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                rows={4}
                                placeholder={t('dealer.form.message.placeholder')}
                                className="w-full bg-[#13100B]/50 lg:bg-background/50 border border-white/10 md:border-white/20 lg:border-white/10 py-2 md:py-2.5 lg:py-4 px-4 md:px-5 lg:px-6 text-[16px] lg:text-[10px] font-bold uppercase tracking-widest text-white focus:outline-none focus:border-primary resize-none min-h-[80px] lg:min-h-[120px] placeholder:text-[10px] md:placeholder:text-[11px] lg:placeholder:text-[10px]"
                              />
                            </div>

                            <div className="pt-2 md:pt-4 flex gap-3 md:gap-4">
                              <button 
                                type="button"
                                onClick={prevStep}
                                className="mirror-button-outline flex-1 clip-path-none group border-white/10 text-white/50 hover:text-white hover:border-white/20 !px-3 !py-2.5 md:!px-4 md:!py-3 lg:!px-8 lg:!py-4"
                              >
                                <span className="flex items-center gap-2 md:gap-3 relative z-10 text-[11px] md:text-sm lg:text-[10px] whitespace-nowrap">
                                  <ArrowLeft size={14} className="group-hover:-translate-x-1" /> {t('dealer.form.wizard.back')}
                                </span>
                              </button>
                              <button 
                                type="submit" 
                                disabled={isSubmitting}
                                className="mirror-button flex-[2] clip-path-none group !px-3 !py-2.5 md:!px-4 md:!py-3 lg:!px-8 lg:!py-4"
                              >
                                <span className="flex items-center gap-2 md:gap-3 relative z-10 text-[11px] md:text-sm lg:text-[10px] whitespace-nowrap">
                                  {isSubmitting ? '...' : t('dealer.form.wizard.submit')} <Send size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1" />
                                </span>
                              </button>
                            </div>
                            <p className="text-[7px] md:text-[8px] uppercase tracking-widest text-white/20 text-center">
                              {t('dealer.form.disclaimer')}
                            </p>
                          </div>
                        )}
                    </form>
                  </div>
                ) : (
                  <div 
                    className="py-12 flex flex-col items-center text-center space-y-8"
                  >
                    <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center border border-primary/20 relative">
                      <div>
                        <CheckCircle2 size={48} className="text-primary" />
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h3 className="text-3xl font-extrabold uppercase tracking-tighter text-white">{t('dealer.form.success.title')}</h3>
                      <p className="text-white/50 text-sm uppercase tracking-widest leading-relaxed max-w-sm mx-auto">
                        {t('dealer.form.success.desc')}
                      </p>
                    </div>
                    <div className="flex flex-col w-full gap-4">
                      <button 
                        onClick={resetForm}
                        className="mirror-button w-full clip-path-none"
                      >
                        <span className="relative z-10">{t('dealer.form.success.reset')}</span>
                      </button>
                      <button 
                        onClick={() => setIsSubmitted(false)}
                        className="text-[10px] font-bold uppercase tracking-widest text-white/30 hover:text-white"
                      >
                        {t('dealer.form.success.back')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};